import { getHeroesBySearchCriteria } from '../../services/hero.service'
import { getProbabilityThatHeroesCanSummonTargetGene } from '../../helpers/genes.helpers'
import { ToPrice } from '../../helpers/format.helpers'

export const sortAndFilterHeroes = (heroes, filters, sortBy, summonClass) => {
    let sortedHeroes = heroes

    // Skip filtering and sorting if there are no records
    if (!heroes.length) return sortedHeroes

    // Filter based on filter criteria
    sortedHeroes = sortedHeroes.filter(hero => {
        const remainingSummons =
            !filters.summonsRemaining || Number(hero.summonsRemaining) >= Number(filters.summonsRemaining)
        const maxSummons = !filters.maxSummons || Number(hero.maxSummons) >= Number(filters.maxSummons)
        const minGen = !filters.minGen || filters.minGen === '' || Number(hero.generation) >= Number(filters.minGen)
        const maxGen = filters.maxGen === '' || Number(hero.generation) <= Number(filters.maxGen)
        const minProbability = !filters.minProbability || filters.minProbability === '' || hero.targetProbability * 100 >= filters.minProbability
        const maxTotalCost = !filters.maxTotalCost || filters.maxTotalCost === '' || hero.price + hero.summonCost <= filters.maxTotalCost
        const mainClass = filters.includeSummonClass || hero.mainClass !== summonClass
        const classMatch = !filters.classMatch || hero.mainClassGenes[0] === hero.mainClassGenes[1]
        const subclassMatch = !filters.subclassMatch || hero.subClassGenes[0] === hero.subClassGenes[1]
        const professionMatch = !filters.professionMatch || hero.professionGenes[0] === hero.professionGenes[1]

        const probabilityMatch = filters.includeAllHeroes || hero.classMatch

        const traitLevel =
            filters.traitFocus === 'all'
                ? 0
                : filters.traitFocus === 'advanced'
                ? 16
                : filters.traitFocus === 'elite'
                ? 24
                : 28
        const traitFocus =
            filters.traitFocus === 'all' ||
            hero.active1 >= traitLevel ||
            hero.active2 >= traitLevel ||
            hero.passive1 >= traitLevel ||
            hero.passive2 >= traitLevel

        return (
            maxSummons &&
            remainingSummons &&
            minGen &&
            maxGen &&
            minProbability &&
            maxTotalCost &&
            mainClass &&
            classMatch &&
            subclassMatch &&
            professionMatch &&
            traitFocus &&
            probabilityMatch
        )
    })

    // Sort heroes by probability or price
    switch (sortBy) {
        case 'probability':
            return sortedHeroes.sort(sortByProbability)
        case 'price':
            return sortedHeroes.sort(sortByPrice)
        default:
            return sortedHeroes
    }
}

const sortByProbability = (a, b) => {
    return a.targetProbability > b.targetProbability ? -1 : a.targetProbability < b.targetProbability ? 1 : 0
}

const sortByPrice = (a, b) => {
    const aPrice = a.price + (a.auctionType === 'sale' ? 0 : a.summonCost)
    const bPrice = b.price + (b.auctionType === 'sale' ? 0 : b.summonCost)
    return aPrice > bPrice ? 1 : aPrice < bPrice ? -1 : 0
}

// Retrieve a page of matching heroes from the Tavern and return the ones that match
export const getSummoningProbability = async (mainHero, options, pageSize, offset) => {
    const listedHeroes = await getHeroesBySearchCriteria(options, pageSize, offset)

    // Analyze each of the heroes in auction
    for (let i = 0; i < listedHeroes.length; i++) {
        const heroToAnalyze = listedHeroes[i]
        heroToAnalyze.auctionType = options.auctionType
        heroToAnalyze.price = ToPrice(heroToAnalyze.price)

        let classProbability = 1
        let subclassProbability = 1
        let professionProbability = 1
        let mainClassMatch = true
        let subClassMatch = true
        let professionMatch = true

        // Apply the mainclass probability, if applicable
        {
            const a = getProbabilityThatHeroesCanSummonTargetGene(
                mainHero.mainClassGenes,
                heroToAnalyze.mainClassGenes,
                options.summonClass
            )
            classProbability = a ? a.value : 0
            
            mainClassMatch = options.mainClasses.some(item => {
                return heroToAnalyze.mainClassGenes.includes(item.name)
            })
        }

        // Apply the subclass probability, if applicable
        if (options.summonSubclass !== 'all') {
            const b = getProbabilityThatHeroesCanSummonTargetGene(
                mainHero.subClassGenes,
                heroToAnalyze.subClassGenes,
                options.summonSubclass
            )
            subclassProbability = b ? b.value : 0
            
            subClassMatch = options.subClasses.some(item => {
                return heroToAnalyze.subClassGenes.includes(item.name)
            })
        }

        // Apply the profession probability, if applicable
        if (options.profession !== 'all') {
            const c = getProbabilityThatHeroesCanSummonTargetGene(
                mainHero.professionGenes,
                heroToAnalyze.professionGenes,
                options.profession
            )
            professionProbability = c ? c.value : 0
            
            professionMatch = heroToAnalyze.professionGenes.includes(options.profession)
        }

        heroToAnalyze.targetProbability = classProbability * subclassProbability * professionProbability
        heroToAnalyze.classMatch = mainClassMatch && subClassMatch && professionMatch
    }

    // Remove any heroes who cannot be used to summon the target class
    // Unless looking up from a wallet
    const filteredHeroes = options.wallets.length ? listedHeroes : listedHeroes.filter(hero => hero.targetProbability > 0)

    return [filteredHeroes, listedHeroes.length]
}
